import { useState } from 'react';
import { useWeightRecords, type WeightRecord } from '@/hooks/useWeightRecords';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Trash2, Scale, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { formatWeight, shouldUseGrams, toKg, fromKg, suggestUnit, type WeightUnit } from '@/utils/weightFormatting';

interface WeightHistoryTabProps {
  petId: string;
}

export function WeightHistoryTab({ petId }: WeightHistoryTabProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { records, isLoading, updateRecord, deleteRecord } = useWeightRecords(petId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editWeight, setEditWeight] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editUnit, setEditUnit] = useState<WeightUnit>('kg');

  const allWeights = records.map(r => Number(r.weight_kg));
  const useGrams = shouldUseGrams(allWeights);
  const dateFormatter = new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const formatMeasurementDate = (date: string) => dateFormatter.format(new Date(`${date}T00:00:00`));

  const chartData = [...records]
    .sort((a, b) => new Date(a.measurement_date).getTime() - new Date(b.measurement_date).getTime())
    .map(r => ({
      date: formatMeasurementDate(r.measurement_date),
      weight: useGrams ? Math.round(Number(r.weight_kg) * 1000) : Number(r.weight_kg),
    }));

  const startEdit = (record: WeightRecord) => {
    const unit = suggestUnit(Number(record.weight_kg));
    setEditingId(record.id);
    setEditUnit(unit);
    setEditWeight(String(fromKg(Number(record.weight_kg), unit)));
    setEditDate(record.measurement_date);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditWeight('');
    setEditDate('');
  };

  const handleUpdate = async (id: string) => {
    const inputNum = parseFloat(editWeight);
    const isG = editUnit === 'g';
    const minVal = isG ? 1 : 0.01;
    const maxVal = isG ? 999999 : 1500;

    if (isNaN(inputNum) || inputNum < minVal || inputNum > maxVal) {
      toast({ title: t('weightTracker.invalidWeight'), variant: 'destructive' });
      return;
    }

    try {
      await updateRecord.mutateAsync({
        id,
        weight_kg: toKg(inputNum, editUnit),
        measurement_date: editDate,
      });
      toast({ title: t('weightTracker.updated') });
      cancelEdit();
    } catch {
      toast({ title: t('weightTracker.updateError'), variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRecord.mutateAsync(id);
      toast({ title: t('weightTracker.deleted') });
    } catch {
      toast({ title: t('weightTracker.deleteError'), variant: 'destructive' });
    }
  };

  if (isLoading) {
    return <div className="p-4 text-center text-muted-foreground">{t('common.loading')}</div>;
  }

  const yAxisUnit = useGrams ? ' g' : ' kg';

  return (
    <div className="space-y-6">
      {/* Chart */}
      {chartData.length >= 2 ? (
        <div className="bg-card p-4 rounded-lg border shadow-sm">
          <h3 className="font-medium text-pet-purple mb-3">{t('weightTracker.chartTitle')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit={yAxisUnit} />
              <Tooltip formatter={(value: number) => [`${value}${yAxisUnit}`, t('weightTracker.weightLabel')]} />
              <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : chartData.length === 1 ? (
        <div className="bg-card p-4 rounded-lg border shadow-sm text-center text-sm text-muted-foreground">
          {t('weightTracker.needMoreData')}
        </div>
      ) : null}

      {/* Table */}
      {records.length > 0 ? (
        <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b">
              <tr>
                <th className="text-left p-3 font-medium">{t('weightTracker.dateLabel')}</th>
                <th className="text-left p-3 font-medium">{t('weightTracker.weightLabel')}</th>
                <th className="text-right p-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {records.map(record => (
                <tr key={record.id}>
                  {editingId === record.id ? (
                    <>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1 items-center">
                          <Input
                            type="number"
                            step={editUnit === 'g' ? 1 : 0.01}
                            value={editWeight}
                            onChange={(e) => setEditWeight(e.target.value)}
                            className="h-8 text-sm w-24"
                          />
                          <div className="flex rounded-md border border-input overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setEditUnit('kg')}
                              className={`px-2 py-1 text-xs font-medium transition-colors ${
                                editUnit === 'kg'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-background text-muted-foreground'
                              }`}
                            >
                              kg
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditUnit('g')}
                              className={`px-2 py-1 text-xs font-medium transition-colors ${
                                editUnit === 'g'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-background text-muted-foreground'
                              }`}
                            >
                              g
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUpdate(record.id)}
                            className="text-primary hover:text-primary h-8 w-8 p-0"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEdit}
                            className="text-muted-foreground hover:text-muted-foreground h-8 w-8 p-0"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-3">{formatMeasurementDate(record.measurement_date)}</td>
                      <td className="p-3">{formatWeight(Number(record.weight_kg))}</td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(record)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(record.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-muted/50 p-8 rounded-lg border border-dashed border-border text-center">
          <Scale className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">{t('weightTracker.noRecords')}</p>
        </div>
      )}
    </div>
  );
}
